import {
  ActivityGraphProvider,
  ActivityGraphTab,
} from './ActivityGraphContext';
import { ActivityGraphTabs } from './ActivityGraphTabs';
import { AIPanel } from './AIPanel';
import { AILoadingPanel } from '@/components/ai-suggestions';
import { cn } from '@/lib/utils';
import { SystemEntity } from '@erp/shared-types';
import { useSuggestedActions } from '@/api/rpc/hooks/ai/useSuggestedActions';

export interface ActivityGraphProps {
  entityType: SystemEntity;
  entityId: string;
  conversationId?: string | null;
  className?: string;
  defaultTab?: ActivityGraphTab;
  /** When true, hides the AI suggestions panel (e.g. after approval) */
  readOnly?: boolean;
}

export function ActivityGraph({
  entityType,
  entityId,
  conversationId,
  className,
  defaultTab = 'activity',
  readOnly = false,
}: ActivityGraphProps) {
  const isAIEnabled =
    entityType === SystemEntity.AR_STAGED_SALES_ORDER && !readOnly;

  // Lift the query up so we can conditionally render the split panel
  const { actions, emailDraft, isLoading, isAgentRunning } =
    useSuggestedActions(
      isAIEnabled ? entityType : undefined,
      isAIEnabled ? entityId : undefined,
      conversationId,
    );

  const hasResults =
    actions.length > 0 || (emailDraft && emailDraft.status === 'draft');

  // Show the AI panel area when results are ready OR when the agent is running
  const hasAIContent = !isLoading && (hasResults || isAgentRunning);

  return (
    <ActivityGraphProvider defaultTab={defaultTab}>
      <div className={cn('h-full flex flex-col min-h-0', className)}>
        {hasAIContent ? (
          <>
            <div className="max-h-[50%] shrink-0 overflow-hidden">
              {hasResults ? (
                <AIPanel
                  entityType={entityType}
                  entityId={entityId}
                  conversationId={conversationId}
                  actions={actions}
                  emailDraft={emailDraft}
                />
              ) : (
                <AILoadingPanel />
              )}
            </div>

            <ActivityGraphTabs
              entityType={entityType}
              entityId={entityId}
              conversationId={conversationId}
              className="min-h-0 flex-1"
            />
          </>
        ) : (
          <ActivityGraphTabs
            entityType={entityType}
            entityId={entityId}
            conversationId={conversationId}
            className="h-full"
          />
        )}
      </div>
    </ActivityGraphProvider>
  );
}
